import { useMemo } from 'react';
import { RailTopTube } from '../parametric';
import { type RailPlacement, SCALE_IN_TO_UNIT } from '../types';
import type { HardwareGroup, HardwarePlacement } from './instances';
import { BalusterField } from './BalusterField';

interface Props {
  placement: RailPlacement;
}

const BALUSTER_SPACING_IN = 4;

/**
 * Build balusters with WORLD-relative positions (taking pitch + rotationY into account),
 * so balusters can be rendered OUTSIDE the rotated rail group and remain vertical.
 *
 * User: "la grille n'est pas dans la bonne angle bras 1 doit suivre plateforme".
 * → Balusters (verticaux) doivent rester perpendiculaires au sol/plateforme,
 *   pas inclinés avec la pente du rail.
 */
function buildBalusterGroup(placement: RailPlacement): HardwareGroup {
  const placements: HardwarePlacement[] = [];
  const count = Math.floor(placement.lengthIn / BALUSTER_SPACING_IN);
  const startSlope = -(placement.lengthIn / 2);
  const pitch = placement.pitchAngle ?? 0;
  const rotY = placement.rotationY;
  const cosP = Math.cos(pitch);
  const sinP = Math.sin(pitch);
  const cosY = Math.cos(rotY);
  const sinY = Math.sin(rotY);

  for (let i = 0; i <= count; i++) {
    const slope = startSlope + i * BALUSTER_SPACING_IN;
    // Position le long du rail incliné (local slope coords)
    const localY = -slope * sinP; // décalage Y dû au pitch
    const localZ = slope * cosP; // décalage Z après pitch
    // Application rotationY (rotation autour de Y, X reste 0)
    const worldX = localZ * sinY;
    const worldZ = localZ * cosY;
    placements.push({
      position: { x: worldX, y: localY, z: worldZ },
      rotation: { x: 0, y: 0, z: 0 }, // VERTICAL dans le world (pas tilté avec le rail)
      parentRefId: placement.id,
    });
  }

  return {
    partNumber: 'MTR065075031',
    geometry: 'baluster-mtr065075031',
    placements,
  };
}

export function Rail({ placement }: Props) {
  const railHIn = placement.heightIn;
  const railH = railHIn * SCALE_IN_TO_UNIT;
  const pitch = placement.pitchAngle ?? 0;

  const balusterGroup = useMemo(
    () =>
      placement.type === 'barrotin' || placement.type === 'barrotin_mains'
        ? buildBalusterGroup(placement)
        : null,
    [placement],
  );

  return (
    <>
      {/* Rails + handrail tubes : ces éléments suivent la pente du rail */}
      <group
        position={[placement.position.x, placement.position.y, placement.position.z]}
        rotation={[pitch, placement.rotationY, 0]}
      >
        <RailTopTube
          lengthIn={placement.lengthIn}
          position={[0, railH, 0]}
          rotation={[0, 0, 0]}
          refId={`${placement.id}-top`}
        />
        <RailTopTube
          lengthIn={placement.lengthIn}
          position={[0, railH * 0.55, 0]}
          rotation={[0, 0, 0]}
          refId={`${placement.id}-mid`}
        />
      </group>
      {/* Balusters : rendus EN DEHORS du group rotated pour rester verticaux.
          Positions pré-calculées en world coords (pitch + rotY appliqués manuellement). */}
      {balusterGroup && (
        <group position={[placement.position.x, placement.position.y, placement.position.z]}>
          <BalusterField group={balusterGroup} heightIn={railHIn} />
        </group>
      )}
    </>
  );
}
